import { Dialog } from "@cmsgov/ds-medicare-gov";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  ActionType,
  AppState,
  PrescriptionDrug,
  ThirdPartyIntegrations,
  PharmacyInfo,
  SearchResultPlan,
} from "../../@types";
import { RouteComponentProps, useLocation, withRouter } from "react-router";
import { Routes } from "../../app";
import { AppContext } from "../../app/store";
import {
  AppPage,
  CoverageSelector,
  HomeIcon,
  LoadingMask,
} from "../../components";
import { hasMailOrderPharmacy } from "../../app/store/AppStore";
import { planTypeGa4EventMap } from "../../components/CoverageSelector/constants";
import {
  CoverageSelectorSelectedValues,
  CoverageType,
} from "../../components/CoverageSelector/types";
import {
  CONSTANTS,
  fetchCosts,
  isOverThirtyDaysSinceLogin,
  makePlanLongId,
  useTranslate,
} from "../../helpers";
import { updateStates } from "../LandingPage/routingHelpers";
import {
  DrugCardsContainer,
  ExtraHelpCardContainer,
  PharmacyCardsContainer,
  SubSection,
  SummaryPageHeader,
} from "./components";
import {
  FindPlansCard,
  YearOverYearCompareCard,
} from "./components/plan-card-types";
import PlanCardsContainer from "./components/PlanCardsContainer";
import { SectionIconTypes } from "./components/SubSection";
import { AddPDEs } from "./components/AddPDEs";
import { getYearPartInfo } from "../../helpers/yearPartHelpers";
import { fetchAndSetOECStatusForYearPart } from "../../helpers/beneficiaryInfoHelpers";
import { fetchPlanPreferredPharmacyInfo } from "../../helpers/planHelpers";
import {
  AnalyticsActionType,
  AnalyticsButton,
  Ga4Event,
  Ga4EventType,
} from "../../app/contexts/Analytics/types";

export const extraHelpSectionTestId = "summary-page-extra-help-section";

const SummaryPage: FunctionComponent<RouteComponentProps> = ({ history }) => {
  const {
    state: {
      beneficiary,
      county,
      currentCoverage,
      degradedIntegrations,
      futureLis,
      language,
      lis,
      mailOrderNetworkStatus,
      nextYearCoverage,
      oecStatuses,
      pharmacies,
      pharmacyType,
      prescriptions,
      zipcode,
    },
    state,
    dispatch,
  } = useContext(AppContext);
  const flags = useFlags();
  const { mctCurrentPlanYear, mctYearPart = "" } = flags;
  const { isOutsideOpenEnrollment } = getYearPartInfo(flags);
  // Future LIS card should show except when the year part is OOE
  const futureLisToUseForExtraHelp = isOutsideOpenEnrollment
    ? undefined
    : futureLis;
  const location = useLocation();
  const url = window.location.href.split("/summary?")[0];
  const [showFindPlansModal, setShowFindPlansModal] = useState(false);
  const [showAddPDEsModal, setShowAddPDEsModal] = useState(false);
  const [overThirtyDaysSinceLogin, setOverThirtyDaysSinceLogin] = useState(
    beneficiary ? isOverThirtyDaysSinceLogin(beneficiary) : true
  );
  const [
    currentCoverageHasPreferredPharmacies,
    setCurrentCoverageHasPreferredPharmacies,
  ] = useState<boolean>(false);
  const hasMailOrderPharmacies = hasMailOrderPharmacy({
    pharmacyType,
  } as AppState);
  const pharmacyCount = pharmacies.length + (hasMailOrderPharmacies ? 1 : 0);
  const drugsCount = prescriptions.length;
  const isMissingPharmsOrDrugs = pharmacyCount === 0 || drugsCount === 0;
  const isBEDAPDown = degradedIntegrations.includes(
    ThirdPartyIntegrations.BEDAP
  );
  const shouldShowPharmacyLabels = currentCoverage && pharmacyCount > 0;
  const currentCoverageLongPlanId = currentCoverage
    ? makePlanLongId(currentCoverage)
    : undefined;
  const futurePlan = beneficiary?.future_coverage[0];

  useEffect(() => {
    (async () => {
      if (shouldShowPharmacyLabels && currentCoverage) {
        const drugCostInfo = await fetchCosts({
          state,
          pharmacies,
          plan: currentCoverage,
          mctCurrentPlanYear,
          t,
        });
        const { costs: pharmacyCosts } = drugCostInfo;
        const pharmaciesWithNetworkStatus = [] as PharmacyInfo[];

        pharmacyCosts.forEach(thisPharmacy => {
          if (thisPharmacy.npi || hasMailOrderPharmacies) {
            const {
              npi,
              in_network,
              mail_order,
              preferred,
              ltc,
            } = thisPharmacy;
            if (thisPharmacy.npi === "") {
              dispatch({
                type: ActionType.SET_MAIL_ORDER_PHARMACY_NETWORK_STATUS,
                payload: { preferred, inNetwork: in_network },
              });
            }
            const name =
              pharmacies.find(pharm => pharm.npi === npi)?.name ||
              t("summary_page.pharmacies.pharmacy_card.mail_order_pharmacy");

            pharmaciesWithNetworkStatus.push({
              in_network,
              ltc,
              mail_order,
              name,
              npi,
              preferred,
            });
          }
        });
        const currentPharmacies = pharmacies.map(pharmacy => {
          const sharedPharmacy = pharmaciesWithNetworkStatus.find(
            result => result.npi === pharmacy.npi
          );
          return sharedPharmacy
            ? {
                ...pharmacy,
                preferred: sharedPharmacy.preferred,
                in_network: sharedPharmacy.in_network,
              }
            : pharmacy;
        });

        dispatch({
          type: ActionType.SET_PHARMACIES,
          payload: currentPharmacies,
        });
      }
    })();
  }, [shouldShowPharmacyLabels, currentCoverage]);

  useEffect(() => {
    (async () => {
      await fetchAndSetOECStatusForYearPart({
        mctYearPart,
        currentYear: mctCurrentPlanYear.toString(),
        dispatch,
      });
    })();
  }, [mctCurrentPlanYear, mctYearPart]);

  useEffect(() => {
    (async () => {
      if (currentCoverage) {
        const plansPreferredPharmaciesInfo = await fetchPlanPreferredPharmacyInfo(
          [currentCoverage]
        );
        if (plansPreferredPharmaciesInfo) {
          setCurrentCoverageHasPreferredPharmacies(
            plansPreferredPharmaciesInfo[makePlanLongId(currentCoverage)]
          );
        }
      }
    })();
  }, [currentCoverage]);

  const t = useTranslate();

  const handleFindPlansButtonClick = () => {
    const modalHeading = t("summary_page.links.find_plans_now");

    dispatch({
      type: AnalyticsActionType.SEND_GA4_EVENT,
      settings: {
        event_name: Ga4Event.MODAL_IMPRESSION,
        heading: modalHeading,
      },
    });

    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_action: "modal impression",
        event_label: modalHeading,
        other_props: {
          event_category: Ga4EventType.UI_INTERACTION,
        },
      },
    });

    setShowFindPlansModal(modalStatus => !modalStatus);
  };

  const getNextRoute = () => {
    if (isBEDAPDown) {
      return Routes.summary.lisQuestions;
    } else {
      return isMissingPharmsOrDrugs
        ? Routes.summary.drugSearchPreferences
        : Routes.summary.searchResults;
    }
  };

  const summaryPageDestinationMap: Record<CoverageType, string> = {
    [CoverageType.MAPD]: getNextRoute(),
    [CoverageType.PDP]: getNextRoute(),
    [CoverageType.PDPAndMedigap]: getNextRoute(),
    [CoverageType.MedigapPolicy]: Routes.medigap.landingPage,
    [CoverageType.learnMore]: Routes.coverageWizard.options,
  };

  // Ensure that an MBP handoff failure alert doesn't persist beyond first viewing
  useEffect(() => {
    const resetMbpHandoffFailed = () =>
      dispatch({ type: ActionType.UPDATE_MBP_HANDOFF_FAILED, payload: false });
    window.addEventListener("beforeunload", resetMbpHandoffFailed);
    return () => {
      window.removeEventListener("beforeunload", resetMbpHandoffFailed);
      resetMbpHandoffFailed();
    };
  }, []);

  useEffect(() => {
    beneficiary &&
      setOverThirtyDaysSinceLogin(isOverThirtyDaysSinceLogin(beneficiary));
  }, [beneficiary]);

  const handleCoverageSelectorSubmit = (
    values: CoverageSelectorSelectedValues,
    analyticsButton?: AnalyticsButton
  ) => {
    updateStates(values, dispatch);
    if (values.coverageType) {
      const destination = summaryPageDestinationMap[values.coverageType];
      const linkUrl = `${url}${destination}${location.search}`;

      if (analyticsButton) {
        dispatch({
          type: AnalyticsActionType.SEND_GA4_EVENT,
          settings: {
            event_name: Ga4Event.PLAN_FINDER_STARTED,
            event_type: Ga4EventType.UI_INTERACTION,
            link_text: analyticsButton.text,
            link_url: linkUrl,
            mct_plan_type: planTypeGa4EventMap[values.coverageType],
            mct_plan_year: values.coverageYear,
          },
        });
        dispatch({
          type: AnalyticsActionType.SEND_TEALIUM_EVENT,
          settings: {
            event_action: "Account Summary Page - Plan Search",
            event_label: planTypeGa4EventMap[values.coverageType],
          },
        });
      }
      history.push(destination);
    }
  };

  const toggleAddPDEsModal = () => {
    setShowAddPDEsModal(modalStatus => !modalStatus);
  };

  const handleAddPDEs = (pdes: PrescriptionDrug[]) => {
    dispatch({
      type: ActionType.SET_PRESCRIPTIONS,
      payload: [...state.prescriptions, ...pdes],
    });
    toggleAddPDEsModal();
  };

  const showYearOverYearCard =
    !isOutsideOpenEnrollment &&
    currentCoverage &&
    nextYearCoverage &&
    !futurePlan &&
    !oecStatuses.currentYear &&
    !oecStatuses.nextYear &&
    overThirtyDaysSinceLogin &&
    !isBEDAPDown;

  return beneficiary ? (
    <AppPage
      title={t("summary_page.title")}
      outerClassName="mct-p-summary-page"
      headerExtension={
        <div id="summary-page-navigation" className="mct-hide-print">
          <HomeIcon className="ds-u-margin-right--1 ds-u-color--primary ds-u-font-size--sm" />
          <a
            className="ds-u-color--primary"
            href={CONSTANTS.linkToAccountHome(language)}
          >
            {t("summary_page.links.account_home")}
          </a>
        </div>
      }
    >
      <h1 className="ds-u-visibility--screen-reader">
        {t("summary_page.title")}
      </h1>
      <SummaryPageHeader
        firstName={beneficiary?.firstname[0]}
        overThirtyDaysSinceLogin={overThirtyDaysSinceLogin}
        hasCurrentCoverage={!!currentCoverage}
      />
      <SubSection
        heading={t("summary_page.headings.your_plans")}
        icon={SectionIconTypes.CheckShieldIcon}
        headingId="YourPlansHeading"
      >
        <PlanCardsContainer
          beneficiary={beneficiary}
          county={county}
          currentCoverage={currentCoverage}
          isBEDAPDown={isBEDAPDown}
          nextYearCoverage={nextYearCoverage}
          onFindPlansClick={handleFindPlansButtonClick}
          overThirtyDaysSinceLogin={overThirtyDaysSinceLogin}
          zipcode={zipcode}
        />
      </SubSection>
      {(lis || futureLisToUseForExtraHelp) && (
        <SubSection
          heading={t("summary_page.headings.extra_help")}
          icon={SectionIconTypes.PiggyBankIcon}
          data-testid={extraHelpSectionTestId}
        >
          <ExtraHelpCardContainer
            isBEDAPDown={isBEDAPDown}
            language={language}
            lis={lis}
            futureLis={futureLisToUseForExtraHelp}
            year={mctCurrentPlanYear as string}
          />
        </SubSection>
      )}
      <SubSection
        heading={t("summary_page.headings.your_pharmacies")}
        icon={SectionIconTypes.PharmacyIcon}
        headingId="YourPharmaciesHeading"
      >
        <PharmacyCardsContainer
          hasMailOrderPharmacies={hasMailOrderPharmacies}
          mailOrderNetworkStatus={mailOrderNetworkStatus}
          overThirtyDaysSinceLogin={overThirtyDaysSinceLogin}
          pharmacies={pharmacies}
          planId={currentCoverageLongPlanId}
          planType={currentCoverage?.plan_type}
          planHasPreferredPharmacies={currentCoverageHasPreferredPharmacies}
          shouldShowPharmacyLabels={shouldShowPharmacyLabels}
        />
      </SubSection>
      <SubSection
        heading={t("summary_page.headings.your_drugs")}
        icon={SectionIconTypes.DrugsIcon}
        headingId="YourDrugsHeading"
      >
        <DrugCardsContainer
          drugs={prescriptions}
          onClickAddPDEs={toggleAddPDEsModal}
          planType={currentCoverage?.plan_type}
        />
      </SubSection>
      <SubSection
        hasBorder={false}
        heading={t("summary_page.headings.shop_for_coverage")}
        icon={SectionIconTypes.CheckShieldIcon}
        name="shop-for-coverage"
      >
        <FindPlansCard
          {...{
            baseClassName: "mct-c-summary-page-find-plans",
            county: county?.name,
            currentCoverage,
            mailingAddress: beneficiary.mailing_address,
            onFindPlansClick: handleFindPlansButtonClick,
            state: county?.state,
            zipcode,
          }}
        />

        {showYearOverYearCard && (
          <YearOverYearCompareCard
            data-testid="year-over-year-thirty-card"
            currentCoverage={currentCoverage as SearchResultPlan}
            nextYearCoverage={nextYearCoverage as SearchResultPlan}
          />
        )}
      </SubSection>
      {showFindPlansModal && (
        <Dialog
          onExit={() => {
            dispatch({
              type: AnalyticsActionType.SEND_GA4_EVENT,
              settings: {
                event_name: Ga4Event.MODAL_CLOSED,
                heading: t("summary_page.links.find_plans_now"),
              },
            });
            dispatch({
              type: AnalyticsActionType.SEND_TEALIUM_EVENT,
              settings: {
                event_action: "modal closed",
                event_label: t("summary_page.links.find_plans_now"),
                other_props: {
                  event_category: Ga4EventType.UI_INTERACTION,
                },
              },
            });
            setShowFindPlansModal(modalStatus => !modalStatus);
          }}
          heading={t("summary_page.links.find_plans_now")}
          dialogClass="mct-c-find-plans-dialog e2e-find-plans-dialog"
          headerClassName="mct-c-find-plans-dialog__header"
          data-testid="FindPlansDialog"
        >
          <CoverageSelector
            initialFormValues={{
              zipcode: zipcode || beneficiary?.mailing_address.zipcode,
              fips:
                county?.fips ||
                beneficiary?.mailing_address.fips_state_county ||
                "",
            }}
            onApply={(values, analytics) => {
              setShowFindPlansModal(modalStatus => !modalStatus);
              handleCoverageSelectorSubmit(values, analytics);
            }}
          />
        </Dialog>
      )}
      {showAddPDEsModal && (
        <Dialog
          onExit={toggleAddPDEsModal}
          heading={t("pde_page.add_recently_filled_drugs")}
          size="wide"
        >
          <AddPDEs onAddPDEs={handleAddPDEs} onCancel={toggleAddPDEsModal} />
        </Dialog>
      )}
    </AppPage>
  ) : (
    <LoadingMask loading={true} />
  );
};

export default withRouter(SummaryPage);
